import { inject, Injectable } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { RequestService } from '@df/api/request/request.service';
import { Status } from '@df/core/status';
import { Nexus } from '@tomandco/nexus-api-types';
import { LocalCustomerModel } from '../../../customer/customer.model.decorator';
import { UserImageRole, UserImagesService } from '../../../customer/user-images.service';
import { NProfiles } from './profile.interfaces';

@Injectable({
    providedIn: 'root'
})
export class ProfileService {
    private customerModel = inject(LocalCustomerModel);
    private requestService = inject(RequestService);
    private router = inject(Router);
    private userImagesService = inject(UserImagesService);

    /**
     * Prepares member data by filtering out empty or false values from favoriteColors and disciplines
     */
    prepareMemberData(memberData: NProfiles.IMemberProfile): NProfiles.IMemberProfile {
        return {
            ...memberData,
            favouriteColours: Object.fromEntries(Object.entries(memberData.favouriteColours || {}).filter(([_, value]) => value === true)),
            disciplines: Object.fromEntries(Object.entries(memberData.disciplines || {}).filter(([_, value]) => value === true))
        };
    }

    /**
     * Saves member profile data
     */
    async saveMemberProfile(
        memberData: NProfiles.IMemberProfile,
        memberIndex: number,
        isFamilyMember: boolean,
        submitStatus: Status,
        form?: NgForm,
        profileImage?: File
    ): Promise<void> {
        const profileData = this.customerModel.customerProfile;
        const filteredMemberData = this.prepareMemberData(memberData);

        if (!isFamilyMember) {
            profileData.customer = filteredMemberData;
        } else {
            if (!profileData.familyMembers) {
                profileData.familyMembers = [];
            }
            profileData.familyMembers[memberIndex] = filteredMemberData;
        }

        await this.saveProfiles(profileData, submitStatus, form);

        if (profileImage && this.customerModel.loyaltyId) {
            await this.userImagesService.saveUserImage(
                this.customerModel.loyaltyId,
                isFamilyMember ? UserImageRole.FamilyMember : UserImageRole.Profile,
                profileImage,
                submitStatus,
                isFamilyMember ? memberIndex : undefined
            );
        }
    }

    async saveHorseProfile(
        horseData: NProfiles.IHorseProfile,
        horseIndex: number,
        submitStatus: Status,
        form?: NgForm,
        horseImage?: File
    ): Promise<void> {
        const profileData = this.customerModel.customerProfile;
        if (!profileData.horses) {
            profileData.horses = [];
        }
        profileData.horses[horseIndex] = horseData;

        await this.saveProfiles(profileData, submitStatus, form);
        if (horseImage && this.customerModel.loyaltyId) {
            await this.userImagesService.saveUserImage(
                this.customerModel.loyaltyId,
                UserImageRole.Horse,
                horseImage,
                submitStatus,
                horseIndex
            );
        }
    }

    async saveProfiles(
        profileData: NProfiles.IProfileData,
        submitStatus: Status,
        form?: NgForm,
        redirectOnSuccess: boolean = true
    ): Promise<void> {
        await this.requestService.submitForm(
            form,
            submitStatus,
            {
                path: Nexus.Api.Customer.Save.path,
                data: {
                    customer_profile: JSON.stringify(profileData)
                }
            },
            () => {
                if (redirectOnSuccess) {
                    this.router.navigateByUrl('/account/edit');
                }
            }
        );
    }

    /**
     * Loads member data from customer profile
     */
    loadMemberData(memberIndex: number, isFamilyMember: boolean): NProfiles.IMemberProfile {
        const profileJson = this.customerModel.customerProfile;
        let memberData: NProfiles.IMemberProfile = NProfiles.defaultMemberProfile;

        if (profileJson) {
            try {
                const profileData: NProfiles.IProfileData = profileJson;

                if (!isFamilyMember) {
                    memberData = { ...memberData, ...(profileData.customer ? { ...profileData.customer } : {}) };
                } else if (profileData.familyMembers && profileData.familyMembers[memberIndex]) {
                    memberData = { ...memberData, ...profileData.familyMembers[memberIndex] };
                }
            } catch (error) {
                console.error('Failed to parse customer profile data:', error);
            }
        }

        return memberData;
    }
}
